<!--
  SuperAdminLayout (design-parity PR-2 update).

  Wraps the SuperAdmin surface (/admin/* SA-only routes). Chrome:

    - Shared Topbar (frontend/src/layouts/Topbar.vue) with the SA identity
      pill in the identityPill slot. White chrome (Topbar's default
      background) -- PR-1 dropped the dark-bar look as part of design
      parity. Unchanged here.
    - SA sidebar -- redesigned in PR-2 to mirror the DistributorLayout
      grouped-nav pattern (6 groups, each with an uppercase eyebrow
      <h3> heading, white background, SidebarNavItem rows that bind to
      a per-row summary slot for sub-text + status dot).
    - ImpersonationBanner -- rendered conditionally by the banner itself.

  Sidebar summary: hydrated by useSuperAdminSummaryStore (PR-2). The
  store POSTs to /api/superadmin/dashboard-summary on mount. Each row's
  summary-slot prop wires to summary?.<key> using the locked key
  mapping (see summary key column below).

    Group              Row             Icon                  Summary key
    -----              ---             ----                  -----------
    Dashboard          Dashboard       space_dashboard       dashboard
    Distributors       Distributors    apartment             distributors
                       Risk Watch      warning               risk_watch
    Plans & Pricing    Plans & Pricing sell                  plans
    Money              Money           payments              money
    Access Control     Access Control  admin_panel_settings  access_control
                       Users           group                 (none)
    Tools              Settings        settings              (none)
                       API Tokens      vpn_key               (none)
                       Operations      build                 operations
                       Audit log       history               audit
                       Change notes    new_releases          (none)

  Existing data-testid attributes on outer <li> wrappers
  (sa-nav-dashboard, sa-nav-distributors, sa-nav-risk-watch,
  sa-nav-plans, sa-nav-money, sa-nav-access-control,
  sa-nav-operations, sa-nav-audit) are preserved so PR-R3 tests
  continue to pass.

  Group <section>s use data-testid="sa-sidebar-group-*" for behavioral
  tests that pin the 6-group structure. The SidebarNavItem component
  emits its own dist-sidebar-subtext-<key> / dist-sidebar-dot-<key>
  testids -- shared with the distributor sidebar by design (the
  component is generic).
-->
<template>
    <div class="sa-layout">
        <Topbar
            eyebrow="ThreadFlow Admin"
            :title="topbarTitle"
            data-testid="sa-topbar"
        >
            <template #identityPill>
                <span
                    class="sa-topbar__pill"
                    data-testid="sa-topbar-pill"
                >SUPERADMIN</span>
            </template>
            <!--
              Green Help pill, immediately left of the Profile icon -- the
              same affordance the distributor and storefront surfaces carry
              (Jake: "put it in the top bar next to the profile icon, and
              make it green so it's easy to see"). The two SuperAdmin
              articles were unreachable until this shipped: the API served
              them and the renderer accepted the audience, but nothing in
              the SA chrome opened them.

              A BUTTON THAT OPENS THE FLYOVER, never a link: a SuperAdmin
              mid-edit on a platform setting must not lose the screen they
              are asking about. The target stays contextual through
              `resolveHelpTarget` -- the flyover computes it from the live
              route, so Help from Platform Settings opens the settings
              article. Nothing here resolves or duplicates that.

              `audience="superadmin"` on the flyover below is what points
              this at the SA article tree; the prop is the only thing
              separating it from the distributor copy of this control.
            -->
            <button
                type="button"
                class="sa-topbar__help"
                data-testid="sa-topbar-help"
                aria-label="Help"
                title="Help"
                :aria-expanded="helpFlyoverOpen ? 'true' : 'false'"
                aria-haspopup="dialog"
                @click="openHelpFlyover"
            >
                <v-icon icon="help_outline" size="18" aria-hidden="true" />
                <span>Help</span>
            </button>
            <!--
              Personal Profile entry point (card
              https://trello.com/c/BxskXBad): an icon in the top-right
              corner NEXT TO Log out, in all four apps.
            -->
            <router-link
                to="/admin/profile"
                class="sa-topbar__profile"
                data-testid="sa-topbar-profile"
                title="Profile"
                aria-label="Profile"
            >
                <v-icon icon="account_circle" size="22" />
            </router-link>
            <button
                type="button"
                class="sa-topbar__logout"
                data-testid="sa-topbar-logout"
                @click="logout"
            >
                Log out
            </button>
        </Topbar>

        <div class="sa-banner-slot">
            <impersonation-banner />
        </div>

        <div class="sa-body">
            <aside class="sa-sidebar" data-testid="sa-sidebar">
                <section data-testid="sa-sidebar-group-dashboard">
                    <h3>Dashboard</h3>
                    <ul>
                        <li data-testid="sa-nav-dashboard">
                            <sidebar-nav-item
                                to="/admin"
                                icon="space_dashboard"
                                label="Dashboard"
                                key-name="dashboard"
                                :summary-slot="summary?.dashboard || null"
                            />
                        </li>
                    </ul>
                </section>

                <section data-testid="sa-sidebar-group-distributors">
                    <h3>Distributors</h3>
                    <ul>
                        <li data-testid="sa-nav-distributors">
                            <sidebar-nav-item
                                to="/admin/distributors"
                                icon="apartment"
                                label="Distributors"
                                key-name="distributors"
                                :summary-slot="summary?.distributors || null"
                            />
                        </li>
                        <li data-testid="sa-nav-clients">
                            <sidebar-nav-item
                                to="/admin/clients"
                                icon="groups"
                                label="Clients"
                                key-name="clients"
                                :summary-slot="summary?.clients || null"
                            />
                        </li>
                        <li data-testid="sa-nav-risk-watch">
                            <sidebar-nav-item
                                to="/admin/risk-watch"
                                icon="warning"
                                label="Risk Watch"
                                key-name="risk_watch"
                                :summary-slot="summary?.risk_watch || null"
                            />
                        </li>
                    </ul>
                </section>

                <section data-testid="sa-sidebar-group-plans-pricing">
                    <h3>Plans &amp; Pricing</h3>
                    <ul>
                        <li data-testid="sa-nav-plans">
                            <sidebar-nav-item
                                to="/admin/plans-pricing"
                                icon="sell"
                                label="Plans &amp; Pricing"
                                key-name="plans"
                                :summary-slot="summary?.plans || null"
                            />
                        </li>
                    </ul>
                </section>

                <section data-testid="sa-sidebar-group-money">
                    <h3>Money</h3>
                    <ul>
                        <li data-testid="sa-nav-money">
                            <sidebar-nav-item
                                to="/admin/money"
                                icon="payments"
                                label="Money"
                                key-name="money"
                                :summary-slot="summary?.money || null"
                            />
                        </li>
                        <!--
                          Unit 10 (payments plan) -- the ops surfaces for the
                          Net Checkout credit rail and decorator settlement.
                          Grouped under Money because that is what they are
                          about; each is gated on superadmin.settings to
                          match its endpoints.
                        -->
                        <li data-testid="sa-nav-payments-distributors">
                            <sidebar-nav-item
                                to="/admin/payments/distributors"
                                icon="account_balance"
                                label="Distributor billing"
                                key-name="payments-distributors"
                            />
                        </li>
                        <li data-testid="sa-nav-payments-settlement">
                            <sidebar-nav-item
                                to="/admin/payments/settlement"
                                icon="local_shipping"
                                label="Decorator settlement"
                                key-name="payments-settlement"
                            />
                        </li>
                        <li data-testid="sa-nav-payments-collections">
                            <sidebar-nav-item
                                to="/admin/payments/collections"
                                icon="receipt_long"
                                label="Collections"
                                key-name="payments-collections"
                            />
                        </li>
                        <!--
                          Unit 7's review/release surface. It shipped without
                          a sidebar entry, which left the OPS-08 gate
                          reachable only by typing the URL -- and a gate
                          nobody can find is a gate nobody uses.
                        -->
                        <li data-testid="sa-nav-distributor-invoices">
                            <sidebar-nav-item
                                to="/admin/distributor-invoices"
                                icon="request_quote"
                                label="Distributor invoices"
                                key-name="distributor-invoices"
                            />
                        </li>
                        <li data-testid="sa-nav-payments-reconciliation">
                            <sidebar-nav-item
                                to="/admin/payments/reconciliation"
                                icon="fact_check"
                                label="Order reconciliation"
                                key-name="payments-reconciliation"
                            />
                        </li>
                    </ul>
                </section>

                <section data-testid="sa-sidebar-group-access-control">
                    <h3>Access Control</h3>
                    <ul>
                        <li data-testid="sa-nav-access-control">
                            <!--
                              Access Control routes to the existing ACL
                              admin (reskin pending). The router will
                              redirect to the canonical path; this entry
                              gives the row a real target rather than a
                              dead <a>.
                            -->
                            <sidebar-nav-item
                                to="/admin/access-control"
                                icon="admin_panel_settings"
                                label="Access Control"
                                key-name="access_control"
                                :summary-slot="summary?.access_control || null"
                            />
                        </li>
                        <li data-testid="sa-nav-users">
                            <!--
                              Platform-wide user administration
                              (https://trello.com/c/tRC6K0Zn) -- sits
                              BESIDE the capability x role matrix, not
                              inside it. Replaces the retired
                              /admin/accounts list. No summary slot: the
                              dashboard-summary payload has no users key.
                            -->
                            <sidebar-nav-item
                                to="/admin/users"
                                icon="group"
                                label="Users"
                                key-name="users"
                                :summary-slot="null"
                            />
                        </li>
                    </ul>
                </section>

                <section data-testid="sa-sidebar-group-tools">
                    <h3>Tools</h3>
                    <ul>
                        <li data-testid="sa-nav-settings">
                            <sidebar-nav-item
                                to="/admin/settings"
                                icon="settings"
                                label="Settings"
                                key-name="settings"
                                :summary-slot="null"
                            />
                        </li>
                        <!--
                          API Tokens sits DIRECTLY under Settings because Jake
                          named that position (2026-09-26): "a link directly
                          under the Settings link in the left nav, labeled
                          'API Tokens'". The order is part of the requirement,
                          not incidental -- superadmin-layout-api-tokens-nav.spec.js
                          asserts the index against Settings and Operations, so
                          a reshuffle fails rather than passing quietly.

                          Ungated here, like every other row in this sidebar:
                          the route's beforeEnter (superadmin.settings) is the
                          gate. Adding a v-if to this one entry alone would make
                          it the single inconsistent row.
                        -->
                        <li data-testid="sa-nav-api-tokens">
                            <sidebar-nav-item
                                to="/admin/api-tokens"
                                icon="vpn_key"
                                label="API Tokens"
                                key-name="api-tokens"
                                :summary-slot="null"
                            />
                        </li>
                        <li data-testid="sa-nav-operations">
                            <sidebar-nav-item
                                to="/admin/operations"
                                icon="build"
                                label="Operations"
                                key-name="operations"
                                :summary-slot="null"
                            />
                        </li>
                        <!--
                          Non-integrated suppliers REPLACES the former
                          top-level "SKU setup" entry (Jake, 2026-08-26).
                          SKU setup is now reached THROUGH a supplier,
                          because a SKU lives under a supplier rather
                          than being a top-level concept. There is
                          deliberately no `sa-nav-sku-setup` entry any
                          more -- a scenario asserts it absent so a
                          revert cannot ship both.
                        -->
                        <li data-testid="sa-nav-non-integrated-suppliers">
                            <sidebar-nav-item
                                to="/admin/suppliers/non-integrated"
                                icon="checkroom"
                                label="Non-integrated suppliers"
                                key-name="non-integrated-suppliers"
                                :summary-slot="null"
                            />
                        </li>
                        <li data-testid="sa-nav-audit">
                            <sidebar-nav-item
                                to="/admin/audit"
                                icon="history"
                                label="Audit log"
                                key-name="audit"
                                :summary-slot="null"
                            />
                        </li>
                        <!--
                          Change notes: a help article generated per
                          production release (tools/change-notes/generate.py),
                          so it lives in the SuperAdmin help tree. Ungated here
                          like every other row; the help route's own
                          beforeEnter (help.access) is the gate.
                        -->
                        <li data-testid="sa-nav-change-notes">
                            <sidebar-nav-item
                                to="/admin/help/superadmin/change-notes"
                                icon="new_releases"
                                label="Change notes"
                                key-name="change-notes"
                                :summary-slot="null"
                            />
                        </li>
                    </ul>
                </section>

                <!--
                  Developer: internal integration capability-check surfaces
                  (prototypes, Jake direction 2026-07-01) plus the external
                  API Docs links. Throwaway demos + developer references,
                  gated superadmin.settings like the Tools group above. NOT
                  product features -- surfaced here so they're easy to demo
                  without wiring them into any customer-facing nav.

                  The two API Docs anchors are external links (new tab +
                  noopener) to the SAFRS-rendered Swagger UI (raw JSON:API,
                  at the API root e.g. http://m4.local:5007/api/) and the
                  Stoplight-Elements curated partner-facing docs at
                  /api/admin/api/docs (PR #1765). Both derived from
                  API_BASE_URL; NOT router-links (the SPA nginx-fallback
                  would swallow those cross-origin paths).
                  Boulders: https://trello.com/c/jrD3ZGux (discoverability).
                -->
                <section data-testid="sa-sidebar-group-developer">
                    <h3>Developer</h3>
                    <ul>
                        <li data-testid="sa-nav-prototypes">
                            <sidebar-nav-item
                                to="/admin/prototypes"
                                icon="science"
                                label="All prototypes"
                                key-name="prototypes"
                                :summary-slot="null"
                            />
                        </li>
                        <li data-testid="sa-nav-prototype-wilcom">
                            <sidebar-nav-item
                                to="/admin/prototypes/wilcom-lettering"
                                icon="format_shapes"
                                label="Wilcom Lettering"
                                key-name="prototype_wilcom"
                                :summary-slot="null"
                            />
                        </li>
                        <li data-testid="sa-nav-prototype-artifi">
                            <sidebar-nav-item
                                to="/admin/prototypes/artifi-designer"
                                icon="brush"
                                label="Artifi Designer"
                                key-name="prototype_artifi"
                                :summary-slot="null"
                            />
                        </li>
                        <li data-testid="sa-nav-api-docs">
                            <a
                                class="sa-external-link"
                                :href="safrsSwaggerUrl"
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="sa-nav-api-docs-link"
                            >
                                <span class="sa-external-link__row">
                                    <v-icon
                                        class="sa-external-link__icon"
                                        icon="description"
                                        size="18"
                                    />
                                    <span class="sa-external-link__label">API Docs</span>
                                    <v-icon
                                        class="sa-external-link__external-icon"
                                        icon="open_in_new"
                                        size="14"
                                    />
                                </span>
                            </a>
                        </li>
                        <li data-testid="sa-nav-api-docs-handauthored">
                            <a
                                class="sa-external-link"
                                :href="handAuthoredDocsUrl"
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="sa-nav-api-docs-handauthored-link"
                            >
                                <span class="sa-external-link__row">
                                    <v-icon
                                        class="sa-external-link__icon"
                                        icon="menu_book"
                                        size="18"
                                    />
                                    <span class="sa-external-link__label">API Docs (curated)</span>
                                    <v-icon
                                        class="sa-external-link__external-icon"
                                        icon="open_in_new"
                                        size="14"
                                    />
                                </span>
                            </a>
                        </li>
                    </ul>
                </section>
            </aside>

            <main class="sa-main">
                <slot />
            </main>
        </div>

        <!--
          Help flyover. Mounted at the LAYOUT level so it floats over every
          SA screen and survives navigation; it re-resolves its contextual
          article on each route change rather than being remounted per page.
        -->
        <help-flyover
            audience="superadmin"
            :open="helpFlyoverOpen"
            @close="closeHelpFlyover"
        />
    </div>
</template>

<script>
import { describeError } from "@/utils/safeErrorLog";
import { onMounted, onUnmounted } from "vue";
import ImpersonationBanner from "@/components/ImpersonationBanner.vue";
import SidebarNavItem from "@/layouts/SidebarNavItem.vue";
import Topbar from "@/layouts/Topbar.vue";
import HelpFlyover from "@/components/Help/HelpFlyover.vue";
import { useSuperAdminSummaryStore } from "@/stores/superAdminSummary";

// Longest-prefix-match first so /admin/distributors sub-paths (e.g.
// /admin/distributors/:id/edit) title as "Distributors", not "Overview".
// Order matters: more specific paths come first.
const SA_TITLE_MAP = [
    // Longest prefix first: the nested SKU-setup path must win over the
    // supplier list it sits under, or every SKU page would title
    // "Non-integrated suppliers".
    ["/admin/suppliers/non-integrated/", "SKU Setup"],
    ["/admin/suppliers/non-integrated", "Non-integrated Suppliers"],
    ["/admin/distributors", "Distributors"],
    // A single client's details page (/admin/clients/:id) titles "Client",
    // like the distributor workspace; the list keeps "Clients".
    ["/admin/clients/", "Client"],
    ["/admin/clients", "Clients"],
    ["/admin/risk-watch", "Risk Watch"],
    ["/admin/plans-pricing", "Plans & Pricing"],
    // Longest prefix first: the specific payments pages must win over the
    // /admin/payments prefix they share.
    ["/admin/payments/reconciliation", "Order Reconciliation"],
    ["/admin/payments/distributors", "Distributor Billing"],
    ["/admin/payments/settlement", "Decorator Settlement"],
    ["/admin/payments/collections", "Collections"],
    ["/admin/distributor-invoices", "Distributor Invoices"],
    ["/admin/money", "Money"],
    ["/admin/operations", "Operations"],
    ["/admin/prototypes", "Prototypes"],
    ["/admin/audit", "Audit Log"],
    ["/admin/access-control", "Access Control"],
    // Most general last. /admin and /admin/ both fall through here.
    ["/admin", "Overview"],
];

export default {
    name: "SuperAdminLayout",
    components: { HelpFlyover, ImpersonationBanner, SidebarNavItem, Topbar },
    setup() {
        // SA sidebar summary -- sub-text + status dot data flowing from
        // POST /api/superadmin/dashboard-summary. Fetched on mount; the
        // store is module-scoped Pinia so re-mounts inside the SPA
        // share state until clear() or logout.
        const summaryStore = useSuperAdminSummaryStore();
        // Generic sidebar-refresh subscription (sidebar-emit chain PR-F,
        // https://trello.com/c/g9K52FsA): an SA mutation on a child page emits
        // emitSidebarRefresh('<sa-row-id>') on success; this subscription
        // coarse-refetches the summary when the id is a row this layout owns
        // (SUPERADMIN_KEYS). Torn down on unmount so no listener survives a
        // shell switch. The $route.path watcher below is retained for now (its
        // collapse into pure event-driven refresh is a later PR).
        let offSidebarRefresh = null;
        onMounted(() => {
            summaryStore.fetch();
            offSidebarRefresh = summaryStore.subscribe();
        });
        onUnmounted(() => {
            offSidebarRefresh?.();
        });
        return { summaryStore };
    },
    data() {
        return {
            // Help flyover open state. Owned by the layout because the
            // panel is mounted here and has to outlive any single page.
            helpFlyoverOpen: false,
        };
    },
    computed: {
        summary() {
            return this.summaryStore.summary;
        },
        safrsSwaggerUrl() {
            // SAFRS auto-generates a Swagger 2.0 UI at the API root. The
            // frontend's API_BASE_URL (build-time env, e.g.
            // "http://m4.local:5007/api") IS the API root -- the Swagger
            // UI is served at exactly that URL with a trailing slash.
            //
            // Verified 2026-06-29 against tf-staging:
            //   GET http://m4.local:5007/api/  -> 200, Swagger UI HTML
            //
            // The frontend's own nginx catches /api/ on the 8086 (frontend)
            // port and serves the SPA fallback, so the link MUST point at
            // the API origin/port directly, which API_BASE_URL already is.
            const base = process.env.API_BASE_URL || "/api";
            return base.endsWith("/") ? base : base + "/";
        },
        handAuthoredDocsUrl() {
            // Hand-authored Stoplight Elements docs page shipped in
            // PR #1765. The api_docs_bp blueprint is mounted at
            // /admin/api/docs under the api_bp prefix (/api), so the
            // path off the API origin is /admin/api/docs. API_BASE_URL
            // already includes the /api prefix.
            //
            // Strip any trailing slash on API_BASE_URL before appending
            // so a configured value of "http://m4.local:5007/api/" still
            // produces "http://m4.local:5007/api/admin/api/docs" (no
            // double slash). Mirrors the substring-strip pattern used in
            // safrsSwaggerUrl with the inverse normalization.
            //
            // The frontend's own nginx catches /api/ on the frontend port
            // and serves the SPA fallback, so the link MUST point at the
            // API origin/port directly, same reasoning as the SAFRS link.
            const base = process.env.API_BASE_URL || "/api";
            return base.replace(/\/+$/, "") + "/admin/api/docs";
        },
        topbarTitle() {
            const path = this.$route?.path || "";
            for (const [prefix, title] of SA_TITLE_MAP) {
                // A trailing-slash entry names the pages UNDER a list (it
                // matches only deeper paths); a plain entry matches itself
                // and everything below it.
                const matches = prefix.endsWith("/")
                    ? path.startsWith(prefix) && path.length > prefix.length
                    : path === prefix || path.startsWith(prefix + "/");
                if (matches) {
                    return title;
                }
            }
            return "ThreadFlow Admin";
        },
    },
    mounted() {
        // The sidebar summary is fetched once in setup()'s onMounted and the
        // layout then persists across intra-surface navigations -- so a
        // mutation on a child page leaves the sidebar sub-text stale until a
        // full page reload (#1830). Re-fetch on every in-surface PATH change
        // so navigating away from the mutating page (the natural next step
        // after a save) pulls fresh figures. Watching the PATH (not the whole
        // route) avoids a refetch on pure query-param changes, which never
        // alter the summary.
        this.$watch(
            () => this.$route && this.$route.path,
            (next, prev) => {
                if (next && next !== prev) {
                    this.summaryStore.fetch();
                }
            }
        );
    },
    methods: {
        openHelpFlyover() {
            this.helpFlyoverOpen = true;
        },
        closeHelpFlyover() {
            this.helpFlyoverOpen = false;
        },
        async logout() {
            try {
                await this.$store.dispatch("logout");
                this.$router.push("/login");
            } catch (e) {
                console.error(describeError(e));
            }
        },
    },
};
</script>

<style scoped>
.sa-layout {
    /* Fixed-viewport shell. See DistributorLayout for the full rationale. */
    height: 100%;
    display: flex;
    flex-direction: column;
    background: var(--tf-surface-light);
    overflow: hidden;
}
.sa-banner-slot {
    flex-shrink: 0;
}
/*
 * SA identity pill -- mounts in Topbar's identityPill slot. Tokens-only.
 * The crimson palette differentiates the SA surface from distributor.
 */
.sa-topbar__pill {
    background: var(--tf-accent-crimson);
    color: var(--tf-fg-on-primary);
    padding: 2px 8px;
    border-radius: var(--tf-radius-xs);
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.6px;
}
/*
 * Green Help pill, immediately left of the Profile icon. Same control, same
 * token, same reasoning as `.dist-topbar__help` in DistributorLayout -- the
 * long-form rationale lives there and is not repeated.
 *
 * The short version: `--tf-success` (#00d084) is the token named "green" in
 * the palette and is the wrong one at 2.03:1 on white -- it fails both the
 * AA text minimum and the 3:1 non-text minimum, so a filled pill with a
 * white label would be near-unreadable. `--tf-success-dark` (#2e7d32)
 * carries the white label at 5.13:1 and reads as a shape at the same ratio.
 * Pinned for THIS file by styles/__tests__/help-affordance-contrast.spec.js,
 * which checks the callsite and not just the token.
 */
.sa-topbar__help {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: var(--tf-success-dark, #2e7d32);
    border: 1px solid var(--tf-success-dark, #2e7d32);
    color: var(--tf-fg-on-primary, #ffffff);
    border-radius: var(--tf-radius-xs);
    padding: 4px 12px;
    font-size: 13px;
    font-family: inherit;
    line-height: 1.4;
    cursor: pointer;
    margin-right: 8px;
}

.sa-topbar__help:hover {
    /* Darken rather than lighten on hover: lightening would walk the fill
     * back toward the sub-AA end of the green scale. */
    background: var(--tf-success-darker, #256428);
    border-color: var(--tf-success-darker, #256428);
}

/*
 * Visible keyboard focus. The default UA ring is easy to lose against a
 * saturated fill, so the ring is drawn OUTSIDE the button with an offset.
 */
.sa-topbar__help:focus-visible {
    outline: 2px solid var(--tf-secondary, #133752);
    outline-offset: 2px;
}

/* Personal Profile icon, immediately left of Log out. */
.sa-topbar__profile {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: var(--tf-fg-1);
    text-decoration: none;
    margin-right: 8px;
}

.sa-topbar__profile:hover {
    opacity: 0.75;
}

.sa-topbar__logout {
    background: transparent;
    border: 1px solid var(--tf-border-strong);
    color: var(--tf-fg-1);
    border-radius: var(--tf-radius-xs);
    padding: 4px 12px;
    font-size: 13px;
    cursor: pointer;
}
.sa-topbar__logout:hover {
    background: var(--tf-surface-alt);
}
.sa-body {
    display: flex;
    flex: 1;
    min-height: 0;
}
/*
 * Sidebar background switched from --tf-slate-ink (dark) to --tf-bg
 * (white) as part of design parity with DistributorLayout. The
 * eyebrow <h3> styling mirrors the distributor sidebar's group
 * dividers (uppercase, muted, small).
 */
.sa-sidebar {
    width: 220px;
    flex-shrink: 0;
    background: var(--tf-bg);
    border-right: 1px solid var(--tf-border);
    padding: 16px 0;
    overflow-y: auto;
}
.sa-sidebar h3 {
    font-size: 0.8125rem;
    text-transform: uppercase;
    color: #374151;
    padding: 8px 16px;
    margin: 0;
}
.sa-sidebar ul {
    list-style: none;
    padding: 0;
    margin: 0 0 16px 0;
}
.sa-main {
    flex: 1;
    padding: 24px;
    min-width: 0;
    min-height: 0;
    overflow-y: auto;
}
/*
 * External-link variant for the sidebar -- used for the SAFRS Swagger UI
 * row (and any future external doc links). Visually parallels the
 * SidebarNavItem look (icon + label, 13px, indented sub-text) without
 * pulling in router-link / SidebarNavItem itself (which is
 * router-link-only by design).
 */
.sa-external-link {
    display: flex;
    flex-direction: column;
    gap: 0;
    padding: 6px 16px 6px 16px;
    color: #2d3748;
    text-decoration: none;
    font-size: 13px;
    line-height: 1.4;
    border-left: 3px solid transparent;
}
.sa-external-link:hover {
    background: #edf2f7;
}
.sa-external-link__row {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
}
.sa-external-link__icon {
    flex-shrink: 0;
    color: var(--tf-fg-4);
}
.sa-external-link__label {
    flex: 1;
    min-width: 0;
}
.sa-external-link__external-icon {
    color: var(--tf-fg-4);
    flex-shrink: 0;
    margin-left: auto;
}
</style>
